import type {
  AlgorithmId,
  LevelDataset,
  LevelDefinition,
  SortDirection,
  StageId,
} from "@algoquest/schemas";

/**
 * Phase 15 -- shared factory for all 100 official campaign levels (Master
 * Spec Section 5, Section 41). Every world file (world01...world10) builds
 * its levels by calling `createCampaignLevel(spec)` rather than writing
 * full `LevelDefinition` object literals by hand, mirroring the
 * `createXxxLevel(spec)` pattern already established by the Phase 02-07
 * prototype fixtures (`game/src/levels/linearSearchPrototypeLevels.ts` and
 * its siblings) -- the difference here is one shared factory instead of
 * one factory per algorithm, because several campaign worlds (4, 9, 10)
 * deliberately mix algorithms within a single world to teach comparing
 * and choosing between them (Master Spec Section 5's "Search Challenge",
 * "Sorting Challenge", and "Algorithm Lab" worlds).
 *
 * Every campaign level stays single-algorithm-per-level, consistent with
 * `GameRuntime.loadLevel`'s dispatch (it switches on `dataset.kind`, then
 * throws if `algorithm` is not one of that kind's two/three supported
 * values -- see `game/src/engine/GameRuntime.ts`). Worlds 4, 9 and 10's
 * "challenge" / "combine" framing (Master Spec Section 5) is achieved by
 * varying which algorithm is assigned to each level WITHIN those worlds,
 * plus educational framing in `learningObjectives` and `validation.notes`
 * -- not by any new in-level algorithm-selection or multi-algorithm-
 * chaining mechanic. No phase in the locked phase list (Master Spec
 * Section 43) adds such a mechanic, and Master Spec Section 49 rules out
 * building it speculatively ahead of an actual need.
 */
export interface CampaignLevelSpec {
  id: string;
  world: number;
  levelInWorld: number;
  globalLevelNumber: number;
  algorithm: AlgorithmId;
  values: number[];
  /** Searching levels only (`linear-search` / `binary-search`). */
  target?: number;
  targetPresent?: boolean;
  isSorted?: boolean;
  /** Sorting levels only (`bubble-sort` / `selection-sort` / `insertion-sort`). */
  sortDirection?: SortDirection;
  learningObjectives: string[];
  notes: string;
}

const SEARCHING_ALGORITHMS: ReadonlySet<AlgorithmId> = new Set(["linear-search", "binary-search"]);

/**
 * Grid box spacing scaled down slightly as datasets grow, mirroring the
 * `spacing?: number` overrides already used ad hoc by the Phase 02-07
 * prototype levels (e.g. `linearSearchPrototypeLevels.ts`'s 8-box level
 * using `1.3` instead of the usual `1.6`) -- codified here as one rule
 * instead of a per-level magic number, since the campaign spans 4-15 box
 * datasets rather than the prototypes' narrow 5-8 box range.
 */
function spacingForBoxCount(boxCount: number): number {
  if (boxCount <= 5) return 1.7;
  if (boxCount <= 7) return 1.6;
  if (boxCount <= 9) return 1.4;
  if (boxCount <= 12) return 1.2;
  return 1.0;
}

/**
 * Mirrors the `mechanics` arrays already hand-written per algorithm across
 * `game/src/levels/*PrototypeLevels.ts` -- collected here in one place so
 * every campaign level (whichever world assigns it) gets the exact same
 * mechanics vocabulary its algorithm's prototype level established.
 */
const MECHANICS_BY_ALGORITHM: Record<AlgorithmId, string[]> = {
  "linear-search": ["grid-movement", "nearest-box-query", "sequential-scan"],
  "binary-search": ["grid-movement", "nearest-box-query", "mid-inspection", "direction-choice"],
  "bubble-sort": ["grid-movement", "nearest-box-query", "pair-inspection", "swap-keep-decision"],
  "selection-sort": ["grid-movement", "nearest-box-query", "candidate-scan", "select-skip-decision"],
  "insertion-sort": ["grid-movement", "nearest-box-query", "sorted-region-scan", "shift-insert-decision"],
};

export function createCampaignLevel(spec: CampaignLevelSpec): LevelDefinition {
  const stage: StageId = SEARCHING_ALGORITHMS.has(spec.algorithm) ? "searching" : "sorting";

  let dataset: LevelDataset;
  if (stage === "searching") {
    if (spec.target === undefined || spec.targetPresent === undefined) {
      throw new Error(`createCampaignLevel(${spec.id}): searching levels require target and targetPresent`);
    }
    dataset = {
      kind: "searching",
      values: spec.values,
      target: spec.target,
      isSorted: spec.isSorted ?? false,
      targetPresent: spec.targetPresent,
    };
  } else {
    if (spec.sortDirection === undefined) {
      throw new Error(`createCampaignLevel(${spec.id}): sorting levels require sortDirection`);
    }
    dataset = {
      kind: "sorting",
      values: spec.values,
      sortDirection: spec.sortDirection,
    };
  }

  return {
    id: spec.id,
    version: 1,
    coordinates: {
      stage,
      world: spec.world,
      levelInWorld: spec.levelInWorld,
      globalLevelNumber: spec.globalLevelNumber,
    },
    algorithm: spec.algorithm,
    learningObjectives: spec.learningObjectives,
    dataset,
    layout: {
      boxCount: spec.values.length,
      spacing: spacingForBoxCount(spec.values.length),
      cameraPreset: "elevated-default",
    },
    entities: [],
    mechanics: MECHANICS_BY_ALGORITHM[spec.algorithm],
    tutorialRules: [],
    masteryConditions: {
      maxHintsForMastery: 0,
      maxMistakesForMastery: 0,
    },
    validation: {
      requiresSortedInput: spec.algorithm === "binary-search",
      notes: spec.notes,
    },
  };
}
